import { CardElement, ElementsConsumer } from "@stripe/react-stripe-js";

import poweredByStripe from "../../../assets/images/logo/powered_by_stripe@3x.png";
import axios from "axios";
import { Button } from "../../../components/core/basic/Button";
import { css } from "../../../../styled-system/css";
import { VStack } from "../../../../styled-system/jsx";
import postalCodes from "postal-codes-js";
import React from "react";
import { CountryDropdown, RegionDropdown } from "react-country-region-selector";
import { withTranslation } from "react-i18next";
import { store } from "../../../redux/store";
import { getURLPrefix } from "../../../utils/getUrlPrefix";
import { SUBSCRIPTION_PLANS_UPPERCASE } from "../../../utils/onboardingConstants";
import { PaymentProcessing } from "./PaymentProcessing";
import { trackGaEvent } from "../../../utils/trackGaEvent";
import { trackFacebookPixelEvent } from "../../../utils/trackFacebookPixelEvent";
import ModalAlert from "../../../components/core/basic/ModalAlert";
import { TextField } from "../../../components/fields/base/TextField";

const URL_PREFIX = getURLPrefix();

const subscriptionPlans = SUBSCRIPTION_PLANS_UPPERCASE;

const cardElementOptions = {
  hidePostalCode: true,
  style: {
    base: {
      color: "#850066",
      fontSize: "15px",
      fontWeight: 500,
      fontFamily: "Poppins, sans-serif",
      "::placeholder": {
        color: "#aaa",
      },
    },
    complete: { color: "#850066" },
    invalid: {
      color: "#fb404b",
    },
  },
};

const cardElementWrapperStyle = css({
  border: "input",
  borderRadius: "input",
  paddingX: "medium",
  paddingY: "small",
  backgroundColor: "white",
  "&:focus-within": {
    borderColor: "border.inputFocused",
  },
});

const selectBaseStyle = {
  fontFamily: "Poppins, sans-serif",
  fontSize: "medium",
  fontWeight: 500,
  appearance: "none",
  border: "input",
  borderRadius: "input",
  width: "100%",
  outline: 0,
  paddingY: "small",
  paddingX: "medium",
  backgroundColor: "white",
  backgroundImage:
    "linear-gradient(45deg, transparent 50%, #aaa 50%), linear-gradient(135deg, #aaa 50%, transparent 50%)",
  backgroundRepeat: "no-repeat",
  backgroundSize: "6px 6px, 6px 6px",
  backgroundPosition: "calc(100% - 16px) center, calc(100% - 10px) center",
  "&:focus": {
    borderColor: "border.inputFocused",
  },
};

const labelStyle = css({
  color: "text.label",
  fontSize: "medium",
  fontWeight: "bold",
});

class CheckoutForm extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      subscriptionPlanName: "",
      subscriptionPlan: null,
      payAmount: "",
      payPlan: "",
      payFrequency: "",
      nameOnCard: "",
      invalidNameOnCard: false,
      customer: null,
      country: "",
      region: "",
      postalCode: "",
      validPostalCode: true,
      error: {},
      processingPayment: false,
      subSuccessful: false,
      cardError: false,
      cardComplete: false,
      cardEmpty: true,
    };
  }

  componentDidMount() {
    this.getSubscriptionPlan();
  }

  selectCountry(val) {
    this.setState({ country: val }, () => {
      if (this.state.postalCode !== "") {
        this.verifyPostalCode(this.state.postalCode);
      }
    });
    this.props.setCountry(val);
  }

  selectRegion(val) {
    this.setState({ region: val });
  }

  getSubscriptionPlan() {
    const AuthHeader = "Bearer ".concat(store.getState().auth.tokens.access_token);
    axios
      .get(URL_PREFIX + "/api/aboutyourcompany", {
        params: {},
        headers: { Authorization: AuthHeader },
      })
      .then((result) => {
        const plan =
          this.props.discoveryType === "pricing" ? result.data.pricing_tier.split("__")
          : this.props.discoveryType === "search-analytics" ? result.data.analytics_tier.split("__")
          : this.props.discoveryType === "content" ? result.data.content_tier.split("__")
          : this.props.discoveryType === "advertising" ? result.data.tier.split("__")
          : null;

        if (!plan) {
          this.setState({ tierlessDiscovery: true });
          return;
        }
        if (plan[1] === "ANNUAL") {
          this.setState({
            subscriptionPlan: subscriptionPlans[plan[0]],
            subscriptionPlanName: plan[0],
            payFrequency: plan[1],
            payAmount: subscriptionPlans[plan[0]].annual["pay"],
            payPlan: subscriptionPlans[plan[0]].annual["plan"],
          });
        } else if (plan[1] === "MONTHLY") {
          this.setState({
            subscriptionPlan: subscriptionPlans[plan[0]],
            subscriptionPlanName: plan[0],
            payFrequency: plan[1],
            payAmount: subscriptionPlans[plan[0]].monthly["pay"],
            payPlan: subscriptionPlans[plan[0]].monthly["plan"],
          });
        }
      })
      .catch(() => {
        const { t } = this.props;
        this.setState({
          error: {
            title: t(($) => $.admin["Sorry, failed to fetch your existing subscription plan."]),
            message: t(($) => $.admin["Please reload/refresh the page."]),
          },
        });
      });
  }

  verifyPostalCode(postalCode) {
    if (postalCodes.validate(this.state.country, postalCode) === true) {
      this.setState({ postalCode: postalCode, validPostalCode: true });
    } else {
      this.setState({ postalCode: postalCode, validPostalCode: false });
    }
  }

  handleSubmit = async (event) => {
    event.preventDefault();

    const { stripe, elements, t } = this.props;

    this.setState({ processingPayment: true });

    if (!stripe || !elements) {
      this.setState({
        error: {
          title: t(($) => $.admin["Something's not right"]),
          message: t(($) => $.admin["Please retry after reloading/refreshing the page."]),
        },
      });
    }

    this.createCustomer();
  };

  createCustomer = () => {
    const AuthHeader = "Bearer ".concat(store.getState().auth.tokens.access_token);

    const billingData = {
      country: this.state.country,
      state: this.state.region,
      postal_code: this.state.postalCode,
      line1: this.state.nameOnCard,
      tier: this.state.payPlan,
    };

    axios
      .post(URL_PREFIX + "/api/billing/customer", billingData, {
        params: {},
        headers: { Authorization: AuthHeader },
      })
      .then((result) => {
        this.setState({ customer: result.data.customer }, () => {
          if (localStorage.latestInvoiceId) {
            this.createPaymentMethod(
              this.props.elements.getElement(CardElement),
              this.state.customer,
              store.getState().auth.tokens.access_token,
              "retry",
            );
          } else {
            this.createPaymentMethod(
              this.props.elements.getElement(CardElement),
              this.state.customer,
              store.getState().auth.tokens.access_token,
              "create",
            );
          }
        });
      })
      .catch(() => {
        const { t } = this.props;
        this.setState({
          error: {
            title: t(($) => $.admin["Sorry, something went wrong while charging your card"]),
            message: t(($) => $.admin["Please try again."]),
          },
        });
      });
  };

  createPaymentMethod(cardElement, customerId, auth, action) {
    const { stripe, t } = this.props;
    const billing_details = {
      address: {
        country: this.state.country,
        postal_code: this.state.postalCode,
        state: this.state.region,
      },
      name: this.state.nameOnCard,
    };

    stripe
      .createPaymentMethod({
        type: "card",
        card: cardElement,
        billing_details: billing_details,
      })
      .then((result) => {
        if (result.error) {
          this.setState({
            error: {
              title: t(($) => $.admin["Improper Card Info"]),
              message: result.error.message,
            },
          });
        } else if (action === "create") {
          this.createSubscription({
            customerId: customerId,
            paymentMethodId: result.paymentMethod.id,
            auth: store.getState().auth.tokens.access_token,
            promoCode: this.props.promoCode,
          });
        } else if (action === "retry") {
          this.retryInvoiceWithNewPaymentMethod({
            customerId: customerId,
            paymentMethodId: result.paymentMethod.id,
            auth: store.getState().auth.tokens.access_token,
          });
        }
      });
  }

  createSubscription({ customerId, paymentMethodId }) {
    const paymentInfo = {
      customerId: customerId,
      paymentMethodId: paymentMethodId,
      initialSubscriptionType: this.props.discoveryType,
      promoCode: this.props.promoCode,
    };

    const AuthHeader = "Bearer ".concat(store.getState().auth.tokens.access_token);

    axios
      .post(URL_PREFIX + "/api/billing/subscription", paymentInfo, {
        params: {},
        headers: { Authorization: AuthHeader },
      })
      .then((result) => {
        const { t } = this.props;
        if (result.error) {
          this.setState({
            error: { title: t(($) => $.admin["Card Declined"]), message: t(($) => $.admin["Not sure"]) },
          });
        }
        return result;
      })
      .then((result) => {
        const { t } = this.props;
        if (result.data.status === "User already subscribed.") {
          this.setState({
            error: {
              title: t(($) => $.admin["User Already Subscribed"]),
              message: t(($) => $.admin["Already subscribed"]),
            },
          });
          throw new Error("User already subscribed.");
        }

        if (result.data.status === "Subscription in retry state.") {
          localStorage.setItem("latestInvoiceId", result.data.invoice.id);
          localStorage.setItem("latestInvoicePaymentIntentStatus", result.data.invoice.payment_intent.status);

          this.retryInvoiceWithNewPaymentMethod({
            customerId: customerId,
            paymentMethodId: paymentMethodId,
            auth: store.getState().auth.tokens.access_token,
          });

          return {
            paymentMethodId: "break",
            subscription: "break",
          };
        }

        if (result.data.subscription.status === "active" || result.data.subscription.status === "trialing") {
          trackGaEvent(
            localStorage.extensionSignup ?
              "Extension User Converted to Paying Customer"
            : "App User Converted to Paying Customer",
            "Signup",
          );
          trackFacebookPixelEvent(
            localStorage.extensionSignup ?
              "Extension User Converted to Paying Customer"
            : "App User Converted to Paying Customer",
          );
          if (localStorage.extensionSignup) {
            localStorage.removeItem("extensionSignup");
          }

          this.setState({ subSuccessful: true });
        }

        return {
          paymentMethodId: paymentMethodId,
          subscription: result,
        };
      })
      .then(this.handleRequiresPaymentMethod)
      .catch(() => {
        const { t } = this.props;
        this.setState({
          error: {
            title: t(($) => $.admin["Sorry, something went wrong while trying to charge your card"]),
            message: t(($) => $.admin["Please try again"]),
          },
        });
      });
  }

  handleRequiresPaymentMethod = ({ subscription, paymentMethodId }) => {
    const { t } = this.props;
    if (!(subscription === "break" && paymentMethodId === "break")) {
      const tempSubscription = subscription.data.subscription;
      if (
        tempSubscription.latest_invoice.payment_intent !== null &&
        tempSubscription.latest_invoice.payment_intent.status !== null
      ) {
        if (tempSubscription.latest_invoice.payment_intent.status === "requires_payment_method") {
          localStorage.setItem("latestInvoiceId", tempSubscription.latest_invoice.id);
          localStorage.setItem(
            "latestInvoicePaymentIntentStatus",
            tempSubscription.latest_invoice.payment_intent.status,
          );
          this.setState({
            error: {
              title: t(($) => $.admin["Payment Failed"]),
              message: t(($) => $.admin["Please try a new payment method."]),
            },
          });
        }
      }
    }
  };

  retryInvoiceWithNewPaymentMethod({ customerId, paymentMethodId }) {
    const paymentInfo = {
      customerId: customerId,
      paymentMethodId: paymentMethodId,
      invoiceId: localStorage.getItem("latestInvoiceId"),
    };

    localStorage.removeItem("latestInvoiceId");
    localStorage.removeItem("latestInvoicePaymentIntentStatus");

    const AuthHeader = "Bearer ".concat(store.getState().auth.tokens.access_token);

    return axios
      .post(URL_PREFIX + "/api/billing/retrysubscription", paymentInfo, {
        params: {},
        headers: { Authorization: AuthHeader },
      })
      .then((result) => {
        const { t } = this.props;
        if (result.error) {
          this.setState({
            error: {
              title: t(($) => $.admin["Improper Card Info"]),
              message: t(($) => $.admin["Your card was declined, please use a different card to pay."]),
            },
          });
          throw result;
        }
        return result;
      })
      .then((result) => {
        const { t } = this.props;
        if (result.data.status === "User already subscribed.") {
          this.setState({
            error: {
              title: t(($) => $.admin["User Already Subscribed"]),
              message: t(($) => $.admin["Already subscribed"]),
            },
          });
          throw new Error("User already subscribed.");
        }

        if (result.data.subscription.status === "active" || result.data.subscription.status === "trialing") {
          this.setState({ subSuccessful: true });
        }

        return {
          paymentMethodId: paymentMethodId,
          subscription: result,
        };
      })
      .then(this.handleRequiresPaymentMethod)
      .catch(() => {
        const { t } = this.props;
        this.setState({
          error: {
            title: t(($) => $.admin["Improper Card Info"]),
            message: t(($) => $.admin["Your card was declined, please use a different card to pay."]),
          },
        });
      });
  }

  render() {
    const { stripe, t } = this.props;
    const { country, region } = this.state;
    return (
      <>
        <div className={css({ width: { base: "83%", sm: "67%" }, mx: "auto", maxWidth: "600px" })}>
          <h2
            className={css({
              textAlign: "left",
              color: "darkgrey",
              fontSize: "34px",
              fontWeight: 700,
              marginTop: "xlarge",
            })}
          >
            {t(($) => $.admin["Payment Details"])}
          </h2>
        </div>
        <form onSubmit={this.handleSubmit}>
          <VStack
            gap="xlarge"
            alignItems="stretch"
            className={css({ width: { base: "83%", sm: "67%" }, mx: "auto", maxWidth: "600px", marginTop: "xlarge" })}
          >
            {/* Name on Card */}
            <TextField
              label={t(($) => $.admin["Name on Card"])}
              placeholder={t(($) => $.admin["Name on Card"])}
              value={this.state.nameOnCard}
              onChange={(value) => {
                const NameOnCardRex = /^[a-zA-Z_]+( [a-zA-Z_]+)*$/;
                NameOnCardRex.test(value) === false ?
                  this.setState({ nameOnCard: value, invalidNameOnCard: true })
                : this.setState({ nameOnCard: value, invalidNameOnCard: false });
              }}
              errorMessage={
                this.state.nameOnCard !== "" && this.state.invalidNameOnCard ?
                  t(($) => $.admin["Invalid name"])
                : undefined
              }
            />

            {/* Card Details */}
            <VStack gap="xsmall" alignItems="stretch">
              <label className={labelStyle}>{t(($) => $.admin["Card Details"])}</label>
              <div className={cardElementWrapperStyle}>
                <CardElement
                  options={cardElementOptions}
                  onChange={(event) => {
                    this.setState({
                      cardComplete: !!event.complete,
                      cardEmpty: !!event.empty,
                      cardError: !!event.error,
                    });
                  }}
                />
              </div>
            </VStack>

            {/* Country */}
            <VStack gap="xsmall" alignItems="stretch">
              <label className={labelStyle}>{t(($) => $.admin["Country"])}</label>
              <CountryDropdown
                value={country}
                valueType="short"
                defaultOptionLabel={t(($) => $.admin["Country"])}
                className={css(selectBaseStyle, { color: country ? "text.input" : "grey" })}
                onChange={(val) => this.selectCountry(val)}
              />
            </VStack>

            {/* State/Province */}
            <VStack gap="xsmall" alignItems="stretch">
              <label className={labelStyle}>{t(($) => $.admin["State/Province"])}</label>
              <RegionDropdown
                country={country}
                countryValueType="short"
                defaultOptionLabel={t(($) => $.admin["State/Province"])}
                value={region}
                className={css(selectBaseStyle, { color: region ? "text.input" : "grey" })}
                blankOptionLabel={t(($) => $.admin["State/Province"])}
                onChange={(val) => this.selectRegion(val)}
              />
            </VStack>

            {/* Postal Code */}
            <TextField
              label={t(($) => $.admin["Postal Code"])}
              placeholder={t(($) => $.admin["Postal Code"])}
              value={this.state.postalCode}
              onChange={(value) => this.verifyPostalCode(value)}
              errorMessage={!this.state.validPostalCode ? t(($) => $.admin["Invalid postal code"]) : undefined}
            />

            {/* Submit */}
            {this.state.processingPayment ?
              <Button disabled={true} className={css({ width: "100%", maxWidth: "100%" })}>
                <span>
                  <i className="fa fa-spin fa-circle-o-notch" /> {t(($) => $.admin["Processing your payment"])}
                </span>
              </Button>
            : <Button
                disabled={
                  !stripe ||
                  this.state.nameOnCard === "" ||
                  this.state.invalidNameOnCard ||
                  this.state.country === "" ||
                  this.state.region === "" ||
                  this.state.postalCode === "" ||
                  !this.state.validPostalCode ||
                  !this.state.cardComplete
                }
                className={css({ width: "100%", maxWidth: "100%" })}
                type="submit"
              >
                <span>
                  {this.state.subscriptionPlanName !== "CORPORATE" && !this.state.tierlessDiscovery ?
                    t(($) => $.admin["Subscribe Now (${amount})"], {
                      amount: parseFloat(this.state.payAmount).toFixed(2),
                    })
                  : t(($) => $.admin["Subscribe Now"])}
                </span>
              </Button>
            }
          </VStack>

          <div className={css({ marginTop: "2rem", display: "flex", justifyContent: "center", textAlign: "center" })}>
            <a href="https://stripe.com/" target="_blank" rel="noopener noreferrer">
              <img width="119px" height="26px" src={poweredByStripe} alt="Stripe" />
            </a>
          </div>
        </form>
        {this.state.error.title !== undefined && (
          <ModalAlert
            title={this.state.error.title}
            msg={this.state.error.message}
            onClick={() => this.setState({ error: {}, processingPayment: false })}
          />
        )}
        {this.state.processingPayment && this.state.error.title === undefined && (
          <PaymentProcessing subSuccessful={this.state.subSuccessful} discoveryType={this.props.discoveryType} />
        )}
      </>
    );
  }
}

const TranslatedCheckoutForm = withTranslation(["admin"])(CheckoutForm);

const InjectedCheckoutForm = (props) => {
  return (
    <ElementsConsumer>
      {({ elements, stripe }) => (
        <TranslatedCheckoutForm
          elements={elements}
          stripe={stripe}
          discoveryType={props.discoveryType}
          promoCode={props.promoCode}
          setCountry={props.setCountry}
        />
      )}
    </ElementsConsumer>
  );
};

export default InjectedCheckoutForm;
